import { useState, useEffect, FormEvent } from 'react'
import api from '../../../services/api'
import Button from '../../../components/common/Button'
import './YoutubeVideoForm.css'

interface GalleryCategory {
  id: number
  name: string
  slug: string
}

export interface YoutubeVideo {
  id: number
  title: string
  description: string | null
  youtube_url: string
  video_id: string
  thumbnail_url: string
  embed_url: string
  category_id: number | null
  category_name?: string | null
  position: number
  status: 'draft' | 'published'
  autoplay: boolean
  show_controls: boolean
  loop_video: boolean
  start_time: number | null
  end_time: number | null
  privacy_enhanced: boolean
  created_at: string
  updated_at: string
}

interface YoutubeVideoFormData {
  title: string
  description: string
  youtube_url: string
  category_id: string | number
  position: number
  status: 'draft' | 'published'
  autoplay: boolean
  show_controls: boolean
  loop_video: boolean
  start_time: string
  end_time: string
  privacy_enhanced: boolean
}

interface ParsedUrl {
  video_id: string
  thumbnail_url: string
  embed_url: string
  already_exists: boolean
}

interface YoutubeVideoFormProps {
  video?: YoutubeVideo
  categories: GalleryCategory[]
  onSubmit: (data: YoutubeVideo) => void
  onCancel: () => void
}

function YoutubeVideoForm({ video, categories, onSubmit, onCancel }: YoutubeVideoFormProps) {
  const [formData, setFormData] = useState<YoutubeVideoFormData>({
    title: '',
    description: '',
    youtube_url: '',
    category_id: '',
    position: 0,
    status: 'draft',
    autoplay: false,
    show_controls: true,
    loop_video: false,
    start_time: '',
    end_time: '',
    privacy_enhanced: true,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [parsedUrl, setParsedUrl] = useState<ParsedUrl | null>(null)
  const [parsing, setParsing] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)

  useEffect(() => {
    if (video) {
      setFormData({
        title: video.title || '',
        description: video.description || '',
        youtube_url: video.youtube_url || '',
        category_id: video.category_id || '',
        position: video.position || 0,
        status: video.status || 'draft',
        autoplay: video.autoplay || false,
        show_controls: video.show_controls !== false,
        loop_video: video.loop_video || false,
        start_time: video.start_time?.toString() || '',
        end_time: video.end_time?.toString() || '',
        privacy_enhanced: video.privacy_enhanced !== false,
      })
      // Pre-populate parsed URL data for editing
      setParsedUrl({
        video_id: video.video_id,
        thumbnail_url: video.thumbnail_url,
        embed_url: video.embed_url,
        already_exists: false,
      })
    }
  }, [video])

  const handleParseUrl = async () => {
    if (!formData.youtube_url.trim()) {
      setErrors({ ...errors, youtube_url: 'URL YouTube requise' })
      return
    }

    setParsing(true)
    setErrors({ ...errors, youtube_url: '' })

    try {
      const result = await api.post<ParsedUrl>('/youtube-videos/parse-url', {
        url: formData.youtube_url,
      })
      setParsedUrl(result)

      if (result.already_exists && !video) {
        setErrors({ ...errors, youtube_url: 'Cette video existe deja' })
      }
    } catch (err) {
      setErrors({ ...errors, youtube_url: 'URL YouTube invalide' })
      setParsedUrl(null)
    } finally {
      setParsing(false)
    }
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis'
    }

    if (!formData.youtube_url.trim()) {
      newErrors.youtube_url = 'L\'URL YouTube est requise'
    } else if (!parsedUrl) {
      newErrors.youtube_url = 'Veuillez verifier l\'URL'
    } else if (parsedUrl.already_exists && !video) {
      newErrors.youtube_url = 'Cette video existe deja'
    }

    // Validate time fields
    if (formData.start_time && isNaN(parseInt(formData.start_time))) {
      newErrors.start_time = 'Temps de debut invalide'
    }

    if (formData.end_time && isNaN(parseInt(formData.end_time))) {
      newErrors.end_time = 'Temps de fin invalide'
    }

    if (formData.start_time && formData.end_time) {
      const start = parseInt(formData.start_time)
      const end = parseInt(formData.end_time)
      if (start >= end) {
        newErrors.end_time = 'Le temps de fin doit etre superieur au temps de debut'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    setSubmitting(true)
    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        youtube_url: formData.youtube_url.trim(),
        category_id: formData.category_id || null,
        position: formData.position,
        status: formData.status,
        autoplay: formData.autoplay,
        show_controls: formData.show_controls,
        loop_video: formData.loop_video,
        start_time: formData.start_time ? parseInt(formData.start_time) : null,
        end_time: formData.end_time ? parseInt(formData.end_time) : null,
        privacy_enhanced: formData.privacy_enhanced,
      }

      let result: YoutubeVideo
      if (video) {
        result = await api.put<YoutubeVideo>(`/youtube-videos/${video.id}`, payload)
      } else {
        result = await api.post<YoutubeVideo>('/youtube-videos', payload)
      }

      onSubmit(result)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde')
    } finally {
      setSubmitting(false)
    }
  }

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <form onSubmit={handleSubmit} className="youtube-video-form">
      {/* YouTube URL */}
      <div className="form-field">
        <label htmlFor="youtube_url">
          URL YouTube
          <span className="required">*</span>
        </label>
        <div className="url-input-group">
          <input
            id="youtube_url"
            type="text"
            value={formData.youtube_url}
            onChange={(e) => {
              setFormData({ ...formData, youtube_url: e.target.value })
              setParsedUrl(null) // Reset parsed data on URL change
            }}
            placeholder="https://www.youtube.com/watch?v=..."
          />
          <Button
            type="button"
            variant="outline"
            onClick={handleParseUrl}
            disabled={parsing || !formData.youtube_url.trim()}
          >
            {parsing ? 'Verification...' : 'Verifier'}
          </Button>
        </div>
        {errors.youtube_url && <span className="field-error">{errors.youtube_url}</span>}
        <small className="field-hint">
          Formats acceptes: youtube.com/watch, youtu.be, youtube.com/shorts
        </small>
      </div>

      {/* Video Preview */}
      {parsedUrl && (
        <div className="video-preview">
          <img
            src={parsedUrl.thumbnail_url}
            alt="Apercu video"
            className="thumbnail-preview"
          />
          <div className="preview-info">
            <span className="video-id">ID: {parsedUrl.video_id}</span>
            {parsedUrl.already_exists && !video && (
              <span className="warning-badge">Video deja ajoutee</span>
            )}
          </div>
        </div>
      )}

      {/* Title */}
      <div className="form-field">
        <label htmlFor="title">
          Titre
          <span className="required">*</span>
        </label>
        <input
          id="title"
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Titre de la video"
        />
        {errors.title && <span className="field-error">{errors.title}</span>}
      </div>

      {/* Description */}
      <div className="form-field">
        <label htmlFor="description">Description (optionnel)</label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Description de la video"
          rows={3}
        />
      </div>

      {/* Category */}
      <div className="form-field">
        <label htmlFor="category">Categorie</label>
        <select
          id="category"
          value={formData.category_id}
          onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
        >
          <option value="">Sans categorie</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>
      </div>

      {/* Status */}
      <div className="form-field">
        <label>Statut</label>
        <div className="radio-group">
          <label className="radio-label">
            <input
              type="radio"
              name="status"
              value="draft"
              checked={formData.status === 'draft'}
              onChange={() => setFormData({ ...formData, status: 'draft' })}
            />
            Brouillon
          </label>
          <label className="radio-label">
            <input
              type="radio"
              name="status"
              value="published"
              checked={formData.status === 'published'}
              onChange={() => setFormData({ ...formData, status: 'published' })}
            />
            Publie
          </label>
        </div>
      </div>

      {/* Advanced Options Toggle */}
      <button
        type="button"
        className="advanced-toggle"
        onClick={() => setShowAdvanced(!showAdvanced)}
      >
        {showAdvanced ? '▼' : '▶'} Options avancees
      </button>

      {/* Advanced Options */}
      {showAdvanced && (
        <div className="advanced-options">
          {/* Embed Settings */}
          <div className="form-field">
            <label>Parametres d'integration</label>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.autoplay}
                  onChange={(e) => setFormData({ ...formData, autoplay: e.target.checked })}
                />
                Lecture automatique
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.show_controls}
                  onChange={(e) => setFormData({ ...formData, show_controls: e.target.checked })}
                />
                Afficher les controles
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.loop_video}
                  onChange={(e) => setFormData({ ...formData, loop_video: e.target.checked })}
                />
                Lecture en boucle
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.privacy_enhanced}
                  onChange={(e) => setFormData({ ...formData, privacy_enhanced: e.target.checked })}
                />
                Mode confidentialite (RGPD)
                <small className="checkbox-hint">Utilise youtube-nocookie.com</small>
              </label>
            </div>
          </div>

          {/* Time Settings */}
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="start_time">
                Temps de debut (secondes)
              </label>
              <input
                id="start_time"
                type="number"
                min="0"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                placeholder="0"
              />
              {formData.start_time && (
                <small className="field-hint">
                  = {formatTime(parseInt(formData.start_time) || 0)}
                </small>
              )}
              {errors.start_time && <span className="field-error">{errors.start_time}</span>}
            </div>
            <div className="form-field">
              <label htmlFor="end_time">
                Temps de fin (secondes)
              </label>
              <input
                id="end_time"
                type="number"
                min="0"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                placeholder="Fin"
              />
              {formData.end_time && (
                <small className="field-hint">
                  = {formatTime(parseInt(formData.end_time) || 0)}
                </small>
              )}
              {errors.end_time && <span className="field-error">{errors.end_time}</span>}
            </div>
          </div>

          {/* Position */}
          <div className="form-field">
            <label htmlFor="position">Position (ordre d'affichage)</label>
            <input
              id="position"
              type="number"
              min="0"
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: parseInt(e.target.value) || 0 })}
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="form-actions">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Annuler
        </Button>
        <Button type="submit" variant="primary" disabled={submitting}>
          {submitting ? 'Enregistrement...' : video ? 'Mettre a jour' : 'Ajouter'}
        </Button>
      </div>
    </form>
  )
}

export default YoutubeVideoForm
